import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import { deviceSettingsQuery, nestedTableQuery, post, summaryQuery, type Day, type DeviceSettings, type Saved } from '../api/saveenergee'
import type { Period } from '../api/securitee'
import { ApiError } from '../api/client'
import { escapeHtml } from '../inventoree/format'
import { durationString } from '../easeedesk/format'
import { Spinner } from '../ui/Spinner'
import { toast } from '../ui/notify'
import { NestedTable, type NestedColumn, type NestedState } from '../securitee/NestedTable'
import { activityLabel } from './columns'
import { SettingsCheck } from '../ui/SettingsParts'

/**
 * Randul desfasurat din Saveenergee - `getActionTabs`: Summary (deviceSummary),
 * Activity (deviceActivity + getActivityData), Events (deviceEvents +
 * deviceEventsData), Settings (deviceSettings + profileDetails), cu perioada
 * listei (`from`/`to`) dusa in Summary si Activity.
 */
const TABS = [
  { key: 'summary', label: 'Summary' },
  { key: 'activity', label: 'Activity' },
  { key: 'events', label: 'Events' },
  { key: 'settings', label: 'Settings' },
]

export function SaveenergeeTabs({ id, period }: { id: number; period: Period }) {
  const [tab, setTab] = useState('summary')
  return (
    <div className="row d-flex m-0">
      <div className="col-auto bg-lightest-gray">
        <ul className="action_tabs">
          {TABS.map((t) => (
            <li key={t.key}>
              <a href="#" className={`action_button${tab === t.key ? ' active' : ''}`} onClick={(e) => { e.preventDefault(); setTab(t.key) }}>{t.label}</a>
            </li>
          ))}
        </ul>
      </div>
      <div className="col-md-12">
        <div className="details_content">
          {tab === 'summary' && <SummaryTab id={id} period={period} />}
          {tab === 'activity' && <TableTab id={id} what="activity" columns={ACTIVITY_COLUMNS} period={period} />}
          {tab === 'events' && <TableTab id={id} what="events" columns={EVENT_COLUMNS} />}
          {tab === 'settings' && <SettingsTab id={id} />}
        </div>
      </div>
    </div>
  )
}

function Stat({ color, icon, number, label }: { color: string; icon: string; number: string | number; label: string }) {
  return (
    <div className="col-md-3">
      <div className={`dashboard-stat ${color}`}>
        <div className="visual"><i className={icon}></i></div>
        <div className="details"><div className="number">{number}</div><div className="desc"></div></div>
        <div className="more">{label}</div>
      </div>
    </div>
  )
}

function SavedRow({ title, saved, currency }: { title: string; saved: Saved | null; currency: string }) {
  return (
    <>
      <div className="col-md-12 divider"><h4 className="bordered">{title}</h4></div>
      <Stat color="blue-madison" icon="fa fa-clock" number={saved ? durationString(saved.timeSaved.total) : '0s'} label="Time saved" />
      <Stat color="green-haze" icon="fa fa-bolt" number={`${saved?.energySaved.total ?? 0} kW`} label="Energy saved" />
      <Stat color="blue-mauve" icon="fa fa-money-bill" number={`${saved?.moneySaved.total ?? 0} ${currency}`} label="Money saved" />
      <Stat color="red-intense" icon="fa fa-moon" number={saved ? durationString(saved.timeSaved.autoHibernate) : '0s'} label="Auto hibernate" />
    </>
  )
}

/** devices/details/deviceSummary.php: economia pe perioada, pe ultimul an, si zilele. */
function SummaryTab({ id, period }: { id: number; period: Period }) {
  const q = useQuery(summaryQuery(id, period))
  if (q.error) return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  if (!q.data) return <Spinner />
  const s = q.data
  return (
    <>
      <div className="row">
        <SavedRow title={`Selected period (${period.from} - ${period.to})`} saved={s.saved} currency={s.currency} />
        <SavedRow title="Last year" saved={s.yearly} currency={s.currency} />
      </div>
      <div className="row">
        <div className="col-md-12 divider"><h4 className="bordered">Daily activity</h4></div>
        <div className="col-md-12">
          <table className="table table-striped centered">
            <thead><tr><th>Day</th><th>Running time</th><th>Manual hibernate</th><th>Auto hibernate</th><th>Monitor sleep</th><th>Shut down</th><th>Energy</th><th>Value</th></tr></thead>
            <tbody>
              {s.daily.length === 0 ? (
                <tr><td colSpan={8}>N/A</td></tr>
              ) : s.daily.map((d) => (
                <tr key={d.day}><td>{d.day}</td><td>{durationString(d.active_time)}</td><td>{durationString(d.user_hibernate)}</td><td>{durationString(d.auto_hibernate)}</td><td>{durationString(d.monitor_sleep_time)}</td><td>{durationString(d.shutdown_time)}</td><td>{d.energy} kW</td><td>{d.money} {s.currency}</td></tr>
              ))}
            </tbody>
          </table>
          <p className="color-light-grey">Device {s.watts.device} W, monitor {s.watts.monitor} W, {s.watts.price} {s.currency}/kWh (company settings).</p>
        </div>
      </div>
    </>
  )
}

const ACTIVITY_COLUMNS: NestedColumn[] = [
  { key: 'activity_type', title: 'Type', html: (r) => activityLabel(r.activity_type) },
  { key: 'start_date', title: 'Start', titleHtml: '<i class="far fa-calendar"></i> Start', html: (r) => escapeHtml(r.start_date) },
  { key: 'end_date', title: 'Finish', titleHtml: '<i class="far fa-calendar"></i> Finish', html: (r) => escapeHtml(r.end_date ?? '') },
]

const EVENT_COLUMNS: NestedColumn[] = [
  { key: 'event_date', title: 'Date', titleHtml: '<i class="far fa-calendar"></i> Date', html: (r) => escapeHtml(r.event_date) },
  { key: 'event_message', title: 'Message', html: (r) => String(r.event_message ?? '') },
  { key: 'user_id', title: 'User', html: (r) => (r.Prenume ? escapeHtml(`${r.Prenume} ${r.Nume ?? ''}`) : '<strong>System</strong>') },
]

function TableTab({ id, what, columns, period }: { id: number; what: 'activity' | 'events'; columns: NestedColumn[]; period?: Period }) {
  const dateCol = what === 'activity' ? 1 : 0
  const [state, setState] = useState<NestedState>({ start: 0, length: 10, order: { column: dateCol, dir: 'desc' }, search: '' })
  const col = columns[state.order.column] ?? columns[dateCol]!
  const q = useQuery({
    ...nestedTableQuery(id, what, { draw: 1, start: state.start, length: state.length, orderColumn: state.order.column, orderName: col.key, orderDir: state.order.dir, search: state.search, ...(period ?? {}) }),
    placeholderData: keepPreviousData,
  })
  return (
    <div className="row"><div className="col-md-12">
      <NestedTable id={`saveenergee_${what}`} columns={columns} rows={q.data?.data ?? []} total={q.data?.recordsTotal ?? 0} fetching={q.isFetching} error={q.isError ? (q.error as Error).message : null} state={state} onState={setState} />
    </div></div>
  )
}

export async function send(path: string, fields: Record<string, string>, done: string, onDone?: () => void): Promise<void> {
  try {
    await post(path, fields)
    toast(done, 'Success', 'success')
    onDone?.()
  } catch (err) {
    toast(err instanceof ApiError ? err.message : 'Something went wrong!', 'Error', 'error')
  }
}

/** devices/details/deviceSettings.php: profilul de ore de lucru (setDeviceProfile) si orele masinii (saveDefaultSettings). */
export function SettingsForm({ id, settings, onSaved }: { id: number; settings: DeviceSettings; onSaved(): void }) {
  const [profile, setProfile] = useState(String(settings.profile))
  const days: Day[] = settings.profileDays ?? settings.days
  return (
    <>
      <form className="form-horizontal" onSubmit={(e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        void send(`/saveenergee/devices/${id}/profile`, { profile }, 'Saving profile changed.', onSaved)
      }}>
        <div className="form-group">
          <label className="col-md-3 control-label">Business hours profile</label>
          <div className="col-md-6">
            <select className="form-control" name="profile" value={profile} onChange={(e) => setProfile(e.target.value)}>
              <option value="0">Default (device settings)</option>
              {settings.profiles.map((p) => <option key={p.ID} value={p.ID}>{p.profile_name}</option>)}
            </select>
          </div>
          <div className="col-md-3"><button type="submit" className="btn btn-primary set_profile">Apply profile</button></div>
        </div>
      </form>
      <form className="form-horizontal device_settings" onSubmit={(e: FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        const d = new FormData(e.currentTarget)
        const fields: Record<string, string> = { hibernate: String(d.get('hibernate') ?? ''), monitor_sleep: String(d.get('monitor_sleep') ?? '') }
        for (const day of settings.days) {
          fields[`days[${day.day}][start]`] = String(d.get(`days[${day.day}][start]`) ?? '')
          fields[`days[${day.day}][end]`] = String(d.get(`days[${day.day}][end]`) ?? '')
          if (d.get(`days[${day.day}][checked]`)) fields[`days[${day.day}][checked]`] = 'on'
        }
        void send(`/saveenergee/devices/${id}/settings`, fields, 'Business hours settings saved.', onSaved)
      }}>
        {settings.profileName ? <div className="alert alert-info">Business hours come from the <strong>{settings.profileName}</strong> profile; the days below are read-only until the profile is set back to Default.</div> : null}
        <div className="form-group">
          <label className="col-md-3 control-label">Hibernate after (HH:MM)</label>
          <div className="col-md-3"><input type="time" className="form-control" name="hibernate" defaultValue={settings.hibernate} /></div>
          <label className="col-md-3 control-label">Monitor sleep after (HH:MM)</label>
          <div className="col-md-3"><input type="time" className="form-control" name="monitor_sleep" defaultValue={settings.monitor_sleep} /></div>
        </div>
        <table className="table table-striped centered">
          <thead><tr><th>Day</th><th>Start</th><th>End</th><th>Active</th></tr></thead>
          <tbody>
            {days.map((day) => (
              <tr key={day.day}>
                <td>{day.name}</td>
                <td><input type="time" className="form-control" name={`days[${day.day}][start]`} defaultValue={day.start} readOnly={settings.profileDays !== null} /></td>
                <td><input type="time" className="form-control" name={`days[${day.day}][end]`} defaultValue={day.end} readOnly={settings.profileDays !== null} /></td>
                <td><SettingsCheck aria-label={`${day.name} is on`} name={`days[${day.day}][checked]`} defaultChecked={day.checked} disabled={settings.profileDays !== null} /></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="form-group"><div className="col-md-12"><button type="submit" className="btn btn-primary save_device_settings" disabled={settings.profileDays !== null}>Save</button></div></div>
      </form>
    </>
  )
}

function SettingsTab({ id }: { id: number }) {
  const client = useQueryClient()
  const q = useQuery(deviceSettingsQuery(id))
  if (q.error) return <div className="alert alert-danger">Something went wrong: {q.error.message}</div>
  if (!q.data) return <Spinner />
  return <SettingsForm key={JSON.stringify(q.data)} id={id} settings={q.data} onSaved={() => void client.invalidateQueries({ queryKey: ['saveenergee', 'devices', id] })} />
}
